import type { CSSProperties, FocusEventHandler } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type {
  TextControlBaseInstance,
  TextControlBaseVariants
} from '@elfsight/widget-ui';
import { TextControlBaseContainer } from '@elfsight/widget-ui';
import type { FieldType, FieldValue, FormField } from '../contexts';
import type {
  ChoiceProps,
  CommonFieldProps,
  ConsentProps,
  DateComponentProps,
  DropdownProps,
  FileProps,
  HeadingProps,
  HiddenProps,
  ImageChoiceProps,
  LongTextProps,
  NumberProps,
  NumberScaleProps,
  NumberSliderProps,
  ParagraphProps,
  PhoneProps,
  ShortTextProps,
  StarScaleProps,
  ThumbScaleProps,
  TimeComponentProps,
  URLProps
} from '../fields';
import {
  fieldComponents as defaultComponents,
  fieldValidators
} from '../fields';
import type { ContentDirection } from '../types';
import { FormFieldContainer } from './FormFieldContainer';
import { FormFieldLayout } from './FormFieldLayout';

const OUTSIDE_LABEL_FIELD_TYPES = [
  'file',
  'hidden',
  'label',
  'heading',
  'paragraph',
  'choice',
  'imageChoice',
  'dropdown',
  'consent',
  'starScale',
  'numberScale',
  'thumbScale',
  'smileyScale'
];

const HINT_INSIDE_TYPES = [
  'choice',
  'imageChoice',
  'file',
  'starScale',
  'numberScale',
  'thumbScale',
  'smileyScale'
];

export interface FieldPropsByType {
  choice?: ChoiceProps;
  consent?: ConsentProps;
  date?: DateComponentProps;
  dropdown?: DropdownProps;
  email?: ShortTextProps;
  file?: FileProps;
  heading?: HeadingProps;
  hidden?: HiddenProps;
  imageChoice?: ImageChoiceProps;
  label?: CommonFieldProps;
  longText?: LongTextProps;
  number?: NumberProps;
  numberScale?: NumberScaleProps;
  paragraph?: ParagraphProps;
  phone?: PhoneProps;
  shortText?: ShortTextProps;
  slider?: NumberSliderProps;
  smileyScale?: CommonFieldProps;
  starScale?: StarScaleProps;
  thumbScale?: ThumbScaleProps;
  time?: TimeComponentProps;
  URL?: URLProps;
}

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type FieldComponentProps = {
  field: FormField;
  formSettings: any;
  rowHasLabelInside?: boolean;
  errorColor: string;
  placeholderColor?: string;
  fieldComponents?: Partial<Record<FieldType, any>>;
  contentDirection?: ContentDirection;
  componentFieldProps?: DeepPartial<FieldPropsByType>;
  fieldStylesOnFocus?: CSSProperties;
  fieldStylesOnError?: CSSProperties;
  fieldPadding?: number;
  inputFieldTypes?: FieldType[];
  hintInsideTypes?: FieldType[];
  isOutside?: boolean;
  isHorizontal?: boolean;
  shadow?: boolean;
  columns?: [number, number];
  addBottomMarginToLastChild?: boolean;
  widgetLanguage?: string;
  className?: string;
  invalidErrorMessage?: string;
  requiredErrorMessage?: string;
  variants?: TextControlBaseVariants;
  onChange?: (value?: FieldValue) => void;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
};

export const FieldComponent = forwardRef<
  TextControlBaseInstance,
  FieldComponentProps
>(
  (
    {
      shadow,
      field,
      formSettings,
      rowHasLabelInside,
      errorColor,
      fieldStylesOnFocus,
      fieldStylesOnError,
      fieldComponents = defaultComponents,
      inputFieldTypes = OUTSIDE_LABEL_FIELD_TYPES,
      hintInsideTypes = HINT_INSIDE_TYPES,
      contentDirection = 'column',
      componentFieldProps,
      placeholderColor,
      onChange,
      variants,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { fieldColor, fieldFontStyle, fieldBorderType, fieldActiveColor } =
      formSettings;
    const finalPlaceholderColor =
      placeholderColor || getColorWithAlpha(fieldColor, 0.45);
    const withUnderline = fieldBorderType === 'underline';

    const { id, type, name } = field;
    const Component = fieldComponents[type as keyof typeof fieldComponents];
    const validator = fieldValidators[type as keyof typeof fieldValidators];
    const extraProps =
      componentFieldProps?.[type as keyof FieldPropsByType] ?? {};

    return (
      <FormFieldContainer id={id} validator={validator} onChange={onChange}>
        <FieldLayout
          {...forwardingProps}
          {...formSettings}
          {...field}
          {...extraProps}
          id={id}
          errorColor={errorColor}
          placeholderColor={finalPlaceholderColor}
          name={name || type}
          fieldHintInsideTypes={hintInsideTypes}
          fieldLabelOnlyOutside={inputFieldTypes.includes(type)}
          contentDirection={contentDirection}
          variants={
            !withUnderline
              ? {
                  focused: {
                    ...fieldStylesOnFocus,
                    borderColor: fieldActiveColor
                  },
                  error: {
                    ...fieldStylesOnError,
                    borderColor: errorColor
                  },
                  ...variants
                }
              : undefined
          }
          underlineVariants={
            withUnderline
              ? {
                  focused: {
                    ...fieldStylesOnFocus,
                    borderColor: fieldActiveColor
                  },
                  error: {
                    ...fieldStylesOnError,
                    borderColor: errorColor
                  }
                }
              : undefined
          }
          _spacing={rowHasLabelInside && fieldFontStyle?.fontSize}
        >
          <Component
            ref={forwardingRef}
            errorColor={errorColor}
            shadow={shadow}
            {...field}
            {...extraProps}
          />
        </FieldLayout>
      </FormFieldContainer>
    );
  }
);

const FieldLayout = styled(FormFieldLayout)<{ _spacing: number }>`
  ${({ _spacing }) =>
    !!_spacing &&
    `& ${TextControlBaseContainer} {
      padding: ${_spacing}px !important;
    }`}
`;
